import React, { useEffect, useState } from 'react';
import './Skills.css';
import { getSkills } from '../queries/getSkills';

import {
  FaReact, FaNodeJs, FaAws, FaDocker, FaGitAlt, FaJava, FaPython,
  FaMicrosoft, FaTerminal
} from 'react-icons/fa';
import {
  SiTypescript, SiPostgresql, SiNetlify, SiHtml5,
  SiCss3, SiOpencv, SiTensorflow, SiScikitlearn,
  SiPandas, SiNumpy, SiHuggingface, SiFastapi, SiFlask, SiMongodb,
  SiFirebase, SiSupabase, SiRedis, SiVercel, SiJira,
  SiPostman, SiFigma, SiNotion, SiTrello, SiSlack, SiCplusplus
} from 'react-icons/si';
import { Skill } from '../types';

const iconMap: { [key: string]: JSX.Element } = {
  // AI & Machine Learning Icons
  "Python": <FaPython />,
  "TensorFlow / Keras": <SiTensorflow />,
  "Scikit-Learn": <SiScikitlearn />,
  "Hugging Face Transformers": <SiHuggingface />,
  "LangChain": <SiHuggingface />,
  "OpenCV": <SiOpencv />,
  "Pandas": <SiPandas />,
  "NumPy": <SiNumpy />,
  "Matplotlib": <SiPandas />,
  "RAG / Vector Databases (FAISS, Pinecone)": <SiMongodb />,
  "Deep Learning (CNNs, RNNs, LSTMs, Transformers)": <SiTensorflow />,

  // Frontend Development Icons
  "HTML5": <SiHtml5 />,
  "CSS3": <SiCss3 />,
  "JavaScript (ES6+)": <FaReact />,
  "TypeScript": <SiTypescript />,
  "React.js": <FaReact />,
  "Next.js": <FaReact />,
  "Redux": <FaReact />,
  "Tailwind CSS": <SiCss3 />,
  "Material UI": <SiCss3 />,
  "ShadCN": <SiCss3 />,
  "Framer Motion": <FaReact />,
  "Responsive Design": <SiCss3 />,
  "Accessibility": <SiCss3 />,

  // Backend Development Icons
  "Node.js / Express.js": <FaNodeJs />,
  "FastAPI": <SiFastapi />,
  "Flask": <SiFlask />,
  "Authentication (JWT, OAuth)": <FaNodeJs />,
  "WebSockets / Real-time APIs": <FaNodeJs />,
  "API Design & Integration": <FaNodeJs />,

  // Databases Icons
  "MongoDB (NoSQL)": <SiMongodb />,
  "PostgreSQL (SQL)": <SiPostgresql />,
  "Firebase": <SiFirebase />,
  "Supabase": <SiSupabase />,
  "Redis (for caching)": <SiRedis />,
  "Vector Databases (Pinecone / FAISS)": <SiMongodb />,

  // DevOps & Cloud Icons
  "Docker / Docker Compose": <FaDocker />,
  "Vercel": <SiVercel />,
  "Netlify": <SiNetlify />,
  "Render": <SiVercel />,
  "AWS EC2": <FaAws />,
  "GitHub Actions / CI/CD": <FaGitAlt />,
  "NGINX": <FaAws />,
  "Version Control (Git / GitHub)": <FaGitAlt />,

  // Tools & Workflow Icons
  "VS Code": <FaMicrosoft />,
  "Postman": <SiPostman />,
  "Thunder Client": <SiPostman />,
  "Figma": <SiFigma />,
  "Notion": <SiNotion />,
  "Trello": <SiTrello />,
  "Slack": <SiSlack />,
  "Jira": <SiJira />,
  "CLI & Bash scripting": <FaTerminal />,

  // Programming Languages
  "C++": <SiCplusplus />,
  "Java": <FaJava />,

  // Soft Skills & Other Icons
  "Problem Solving": <FaReact />,
  "Communication": <FaReact />,
  "Team Leadership": <FaReact />,
  "Design Thinking": <SiFigma />,
  "Agile Methodologies": <SiJira />,

  "Startup Mindset": <FaReact />,

  // Additional icons for specific technologies
  "React Native": <FaReact />,
  "Expo Go": <FaReact />,
  "LetsUpgrade": <FaReact />
};

// Updated skills data with your information
const FALLBACK_SKILLS_DATA: Skill[] = [
  // 1. AI & Machine Learning
  {
    name: "Python",
    category: "AI & Machine Learning",
    description: "Core ML programming",
    icon: "Python"
  },
  {
    name: "TensorFlow / Keras",
    category: "AI & Machine Learning",
    description: "Deep learning frameworks",
    icon: "TensorFlow / Keras"
  },
  {
    name: "Scikit-Learn",
    category: "AI & Machine Learning",
    description: "Classical ML models",
    icon: "Scikit-Learn"
  },
  {
    name: "Hugging Face Transformers",
    category: "AI & Machine Learning",
    description: "NLP and LLM fine-tuning",
    icon: "Hugging Face Transformers"
  },
  {
    name: "LangChain",
    category: "AI & Machine Learning",
    description: "Building LLM-powered apps",
    icon: "LangChain"
  },
  {
    name: "OpenCV",
    category: "AI & Machine Learning",
    description: "Computer vision and image processing",
    icon: "OpenCV"
  },
  {
    name: "Pandas",
    category: "AI & Machine Learning",
    description: "Data analysis",
    icon: "Pandas"
  },
  {
    name: "NumPy",
    category: "AI & Machine Learning",
    description: "Numerical computing",
    icon: "NumPy"
  },
  {
    name: "Matplotlib",
    category: "AI & Machine Learning",
    description: "Data visualization",
    icon: "Matplotlib"
  },
  {
    name: "RAG / Vector Databases (FAISS, Pinecone)",
    category: "AI & Machine Learning",
    description: "For intelligent retrieval",
    icon: "RAG / Vector Databases (FAISS, Pinecone)"
  },
  {
    name: "Deep Learning (CNNs, RNNs, LSTMs, Transformers)",
    category: "AI & Machine Learning",
    description: "Advanced neural networks",
    icon: "Deep Learning (CNNs, RNNs, LSTMs, Transformers)"
  },

  // 2. Frontend Development
  {
    name: "HTML5",
    category: "Frontend Development",
    description: "Markup language for web content",
    icon: "HTML5"
  },
  {
    name: "CSS3",
    category: "Frontend Development",
    description: "Styling and layout",
    icon: "CSS3"
  },
  {
    name: "JavaScript (ES6+)",
    category: "Frontend Development",
    description: "Core programming language",
    icon: "JavaScript (ES6+)"
  },
  {
    name: "TypeScript",
    category: "Frontend Development",
    description: "Typed superset of JavaScript",
    icon: "TypeScript"
  },
  {
    name: "C++",
    category: "Frontend Development",
    description: "System programming language",
    icon: "C++"
  },
  {
    name: "Java",
    category: "Frontend Development",
    description: "Object-oriented programming language",
    icon: "Java"
  },
  {
    name: "React.js",
    category: "Frontend Development",
    description: "Component-based frontend framework",
    icon: "React.js"
  },
  {
    name: "Redux",
    category: "Frontend Development",
    description: "State management",
    icon: "Redux"
  },
  {
    name: "Tailwind CSS",
    category: "Frontend Development",
    description: "Utility-first CSS framework",
    icon: "Tailwind CSS"
  },
  {
    name: "Material UI",
    category: "Frontend Development",
    description: "React component library",
    icon: "Material UI"
  },
  {
    name: "ShadCN",
    category: "Frontend Development",
    description: "Reusable component library",
    icon: "ShadCN"
  },
  {
    name: "Framer Motion",
    category: "Frontend Development",
    description: "Smooth animations",
    icon: "Framer Motion"
  },
  {
    name: "Responsive Design",
    category: "Frontend Development",
    description: "Mobile-first design approach",
    icon: "Responsive Design"
  },
  {
    name: "Accessibility",
    category: "Frontend Development",
    description: "Inclusive web design",
    icon: "Accessibility"
  },
  {
    name: "React Native",
    category: "Frontend Development",
    description: "Cross-platform mobile development",
    icon: "React Native"
  },
  {
    name: "Expo Go",
    category: "Frontend Development",
    description: "Mobile development framework",
    icon: "Expo Go"
  },

  // 3. Backend Development
  {
    name: "Node.js / Express.js",
    category: "Backend Development",
    description: "REST APIs & microservices",
    icon: "Node.js / Express.js"
  },
  {
    name: "FastAPI",
    category: "Backend Development",
    description: "Python API framework",
    icon: "FastAPI"
  },
  {
    name: "Flask",
    category: "Backend Development",
    description: "Lightweight Python web framework",
    icon: "Flask"
  },
  {
    name: "Authentication (JWT, OAuth)",
    category: "Backend Development",
    description: "Secure authentication systems",
    icon: "Authentication (JWT, OAuth)"
  },
  {
    name: "WebSockets / Real-time APIs",
    category: "Backend Development",
    description: "Real-time communication",
    icon: "WebSockets / Real-time APIs"
  },
  {
    name: "API Design & Integration",
    category: "Backend Development",
    description: "Building scalable APIs",
    icon: "API Design & Integration"
  },

  // 4. Databases
  {
    name: "MongoDB (NoSQL)",
    category: "Databases",
    description: "Document-based database",
    icon: "MongoDB (NoSQL)"
  },
  {
    name: "PostgreSQL (SQL)",
    category: "Databases",
    description: "Relational database",
    icon: "PostgreSQL (SQL)"
  },
  {
    name: "Firebase",
    category: "Databases",
    description: "Backend-as-a-Service platform",
    icon: "Firebase"
  },
  {
    name: "Supabase",
    category: "Databases",
    description: "Open source Firebase alternative",
    icon: "Supabase"
  },
  {
    name: "Redis (for caching)",
    category: "Databases",
    description: "In-memory data structure store",
    icon: "Redis (for caching)"
  },
  {
    name: "Vector Databases (Pinecone / FAISS)",
    category: "Databases",
    description: "Specialized for AI applications",
    icon: "Vector Databases (Pinecone / FAISS)"
  },

  // 5. DevOps & Cloud
  {
    name: "Docker / Docker Compose",
    category: "DevOps & Cloud",
    description: "Containerization",
    icon: "Docker / Docker Compose"
  },
  {
    name: "Vercel",
    category: "DevOps & Cloud",
    description: "Cloud platform for frontend deployment",
    icon: "Vercel"
  },
  {
    name: "Netlify",
    category: "DevOps & Cloud",
    description: "Web development platform",
    icon: "Netlify"
  },
  {
    name: "Render",
    category: "DevOps & Cloud",
    description: "Unified cloud platform",
    icon: "Render"
  },
  {
    name: "AWS EC2",
    category: "DevOps & Cloud",
    description: "Virtual servers in the cloud",
    icon: "AWS EC2"
  },
  {
    name: "GitHub Actions / CI/CD",
    category: "DevOps & Cloud",
    description: "Automated deployment pipelines",
    icon: "GitHub Actions / CI/CD"
  },
  {
    name: "NGINX",
    category: "DevOps & Cloud",
    description: "Web server",
    icon: "NGINX"
  },
  {
    name: "Version Control (Git / GitHub)",
    category: "DevOps & Cloud",
    description: "Code versioning and collaboration",
    icon: "Version Control (Git / GitHub)"
  },

  // 6. Tools & Workflow
  {
    name: "VS Code",
    category: "Tools & Workflow",
    description: "Code editor",
    icon: "VS Code"
  },
  {
    name: "Postman",
    category: "Tools & Workflow",
    description: "API testing",
    icon: "Postman"
  },
  {
    name: "Thunder Client",
    category: "Tools & Workflow",
    description: "API testing extension",
    icon: "Thunder Client"
  },
  {
    name: "Figma",
    category: "Tools & Workflow",
    description: "Design collaboration",
    icon: "Figma"
  },
  {
    name: "Notion",
    category: "Tools & Workflow",
    description: "Productivity and note-taking",
    icon: "Notion"
  },
  {
    name: "Trello",
    category: "Tools & Workflow",
    description: "Project management",
    icon: "Trello"
  },
  {
    name: "Slack",
    category: "Tools & Workflow",
    description: "Team communication",
    icon: "Slack"
  },
  {
    name: "Jira",
    category: "Tools & Workflow",
    description: "Issue and project tracking",
    icon: "Jira"
  },
  {
    name: "CLI & Bash scripting",
    category: "Tools & Workflow",
    description: "Command-line tools and automation",
    icon: "CLI & Bash scripting"
  },

  // 7. Soft Skills & Other
  {
    name: "Problem Solving",
    category: "Soft Skills / Other",
    description: "Analytical thinking and solutions",
    icon: "Problem Solving"
  },
  {
    name: "Communication",
    category: "Soft Skills / Other",
    description: "Clear and effective communication",
    icon: "Communication"
  },
  {
    name: "Team Leadership",
    category: "Soft Skills / Other",
    description: "Guiding teams to success",
    icon: "Team Leadership"
  },
  {
    name: "Design Thinking",
    category: "Soft Skills / Other",
    description: "User-centered approach to innovation",
    icon: "Design Thinking"
  },
  {
    name: "Agile Methodologies",
    category: "Soft Skills / Other",
    description: "Iterative development practices",
    icon: "Agile Methodologies"
  },

  {
    name: "Startup Mindset",
    category: "Soft Skills / Other",
    description: "Entrepreneurial thinking",
    icon: "Startup Mindset"
  },
  {
    name: "LetsUpgrade",
    category: "Soft Skills / Other",
    description: "Mobile app development experience",
    icon: "LetsUpgrade"
  }
];

const Skills: React.FC = () => {
  const [skillsData, setSkillsData] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSkills() {
      try {
        const data = await getSkills();
        setSkillsData(data);
      } catch (err) {
        console.error("Failed to fetch skills data:", err);
        setError("Failed to load skills data. Showing sample content.");
        // Use fallback data when API request fails
        setSkillsData(FALLBACK_SKILLS_DATA);
      } finally {
        setLoading(false);
      }
    }

    fetchSkills();
  }, []);

  if (loading) return <div className="skills-loading">Loading skills...</div>;

  if (error) {
    console.warn(error);
  }

  // If we still don't have data for some reason, use fallback
  const displayData = skillsData.length > 0 ? skillsData : FALLBACK_SKILLS_DATA;

  // Group skills by category
  const skillsByCategory = displayData.reduce((acc: any, skill: any) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  // Define taglines for each category
  const categoryTaglines: { [key: string]: string } = {
    "AI & Machine Learning": "Building intelligent applications with deep learning and LLMs — bridging AI with real-world web products.",
    "Frontend Development": "Crafting pixel-perfect, responsive, and high-performance user interfaces.",
    "Backend Development": "Building secure, scalable backends that power intelligent user experiences.",
    "Databases": "Designing efficient data architectures for fast, intelligent systems.",
    "DevOps & Cloud": "Automating builds, deployments, and scalability through cloud-native workflows.",
    "Tools & Workflow": "Efficient collaboration through modern tools and organized workflows.",
    "Soft Skills / Other": "Creative, fast-learner, and passionate about solving meaningful problems."
  };

  return (
    <div className="skills-container">
      <div className="skill-category">
        <h2 className="category-title">Vinayak Singh's Skills</h2>
        <p className="category-description">AI & Full Stack Innovator | Building Products that Think, Learn & Scale</p>
      </div>

      {Object.keys(skillsByCategory).map((category, index) => (
        <div key={index} className="skill-category">
          <h3 className={`category-title domain-heading ${category === "AI & Machine Learning" ? "ai-domain-heading" : ""}`}>{category}</h3>
          <p className="category-tagline">{categoryTaglines[category]}</p>
          <div className="skills-grid">
            {skillsByCategory[category].map((skill: any, idx: number) => (
              <div key={idx} className="skill-card">
                <div className="icon">{iconMap[skill.icon] || <FaReact />}</div>
                <h3 className="skill-name">
                  {skill.name.split('').map((letter: any, i: number) => (
                    <span key={i} className="letter" style={{ animationDelay: `${i * 0.05}s` }}>
                      {letter}
                    </span>
                  ))}
                </h3>
                <p className="skill-description">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Skills;